// The thumbnail palettes (DESIGN_SPEC §5; design_logic.js PALS, hx, mix, thumbP).
// A palette is the six colours a wireframe is painted with: background, ink (text), accent,
// two placeholder tones and the light tone used on dark or accent fills.
import type { ColorMode } from './taxonomy';

export type Palette = { bg: string; ink: string; acc: string; ph: string; ph2: string; lt: string };

/** The design's five palettes (PALS). `brand` is its demo organization's (Sharq Textile). */
export const PALETTE_KEYS = ['brand', 'warm', 'sage', 'night', 'mono'] as const;
export type PaletteKey = (typeof PALETTE_KEYS)[number];

const pal = (bg: string, ink: string, acc: string, ph: string, ph2: string, lt: string): Palette => ({ bg, ink, acc, ph, ph2, lt });

export const PALETTES: Record<PaletteKey, Palette> = {
  brand: pal('#F4F1EA', '#22252B', '#1F3A5F', '#E6E0D4', '#CFC6B6', '#FBF8F3'),
  warm: pal('#F3E7D8', '#3A2A20', '#B4532A', '#E7D5C0', '#C9B092', '#FBF6EF'),
  sage: pal('#EEF0E8', '#1F2A22', '#4F6B4A', '#DDE2D3', '#BFC8B3', '#FAFBF7'),
  night: pal('#17181B', '#EDE8DF', '#D9A55B', '#2A2C31', '#3A3C42', '#FBF8F3'),
  mono: pal('#FFFFFF', '#111111', '#111111', '#EDEDED', '#D6D6D6', '#FFFFFF'),
};

export function isPaletteKey(v: unknown): v is PaletteKey {
  return typeof v === 'string' && (PALETTE_KEYS as readonly string[]).includes(v);
}

/** The swatch strip the preferences drawer shows for a palette: bg, ph, acc, ink. */
export function swatches(p: Palette): [string, string, string, string] {
  return [p.bg, p.ph, p.acc, p.ink];
}

/** `#rgb` or `#rrggbb` → `#rrggbb`; anything else (rgb(), names, junk, markup) → null. */
export function normalizeHex(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(s)) return s;
  if (/^#[0-9a-fA-F]{3}$/.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return null;
}

/** hx: `#rrggbb` → [r, g, b]. */
export function hexRgb(h: string): [number, number, number] {
  const x = h.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16)) as [number, number, number];
}

/** mix: the design's linear blend of two colours (t = 0 → a, 1 → b), lowercase `#rrggbb`. */
export function mix(a: string, b: string, t: number): string {
  const A = hexRgb(a);
  const B = hexRgb(b);
  return '#' + A.map((v, i) => Math.round(v + ((B[i] as number) - v) * t).toString(16).padStart(2, '0')).join('');
}

/** A palette with every colour validated; an invalid one falls back to the design's brand palette. */
export function safePalette(p: Palette | null | undefined): Palette {
  if (!p) return PALETTES.brand;
  const out = {} as Palette;
  for (const k of Object.keys(PALETTES.brand) as (keyof Palette)[]) {
    const v = normalizeHex(p[k]);
    if (!v) return PALETTES.brand;
    out[k] = v;
  }
  return out;
}

/** The colours a colour mode paints with (thumbP): inv swaps ink and background, acc uses the accent as background, soft uses PH. */
export function modeColors(p: Palette, mode: ColorMode): Palette {
  const { bg, ink, acc, ph, lt } = p;
  if (mode === 'inv') return { bg: ink, ink: bg, acc: mix(acc, bg, 0.35), ph: mix(ink, bg, 0.14), ph2: mix(ink, bg, 0.26), lt };
  if (mode === 'acc') return { bg: acc, ink: lt, acc: mix(bg, acc, 0.1), ph: mix(acc, '#FFFFFF', 0.2), ph2: mix(acc, '#FFFFFF', 0.34), lt };
  if (mode === 'soft') return { bg: ph, ink, acc, ph: mix(ph, ink, 0.1), ph2: mix(ph, ink, 0.2), lt };
  return p;
}

/** WCAG relative luminance, 0 (black) … 1 (white). */
export function luminance(h: string): number {
  const [r, g, b] = hexRgb(h).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const chroma = (h: string) => {
  const c = hexRgb(h);
  return Math.max(...c) - Math.min(...c);
};

/**
 * «Бренд ‹org›»: a palette from the organization's brand colours (research or the profile).
 * The lightest colour is the background (lightened when it is not light enough), the darkest the ink
 * (darkened when it is not dark enough), the most saturated of the rest the accent; the placeholder
 * and light tones are blends of the two. Null when there is no valid colour.
 */
export function brandPalette(colors: readonly unknown[] | null | undefined): Palette | null {
  const hexes = [...new Set((colors ?? []).map(normalizeHex).filter((x): x is string => !!x).map((x) => x.toUpperCase()))];
  if (!hexes.length) return null;
  const byLum = [...hexes].sort((a, b) => luminance(b) - luminance(a));
  const light = byLum[0] as string;
  const dark = byLum[byLum.length - 1] as string;
  const bg = luminance(light) >= 0.7 ? light : mix(light, '#FFFFFF', 0.85);
  const ink = luminance(dark) <= 0.05 ? dark : mix(dark, '#000000', 0.65);
  const rest = hexes.length > 2 ? hexes.filter((h) => h !== light && h !== dark) : hexes;
  // The accent must read on the background (a button, the icons): of the colours that contrast
  // at least 3:1, the most saturated; otherwise the most contrasting one.
  const score = (h: string) => (contrast(h, bg) >= 3 ? 1000 + chroma(h) : contrast(h, bg));
  const acc = [...rest].sort((a, b) => score(b) - score(a))[0] as string;
  return { bg, ink, acc, ph: mix(bg, ink, 0.07), ph2: mix(bg, ink, 0.17), lt: mix(bg, '#FFFFFF', 0.6) };
}

/**
 * The palette a site's thumbnails paint with: a fixed palette by key, or for `brand` and `auto`
 * («AI решит», by brand) the organization's brand palette, falling back to the design's demo one.
 */
export function resolvePalette(key: string | null | undefined, brandColors?: readonly unknown[] | null): Palette {
  if (key && key !== 'brand' && key !== 'auto' && isPaletteKey(key)) return PALETTES[key];
  return brandPalette(brandColors) ?? PALETTES.brand;
}

/** WCAG contrast ratio of two colours (1 … 21). */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
